import * as yup from 'yup';
import {
  Contribution,
  ContributionClassName,
  ContributionPatchValidationSchema,
  ContributionValidationSchema,
} from 'types/contribution';
import { fieldValidationMessages } from 'app/constants';
import { IPIRegex } from 'app/utils/regex';
import { testIpn } from 'app/utils/utils';
import { SearchableProfile } from 'types/notification.types';
import { StringValidation } from 'types/general.types';
import { isniValidation } from '../../../../utils/validations';

const IPN_MIN_DEFAULT = 10000000;
const IPN_MAX_DEFAULT = 99999999;

const ipnValidation = (pattern?: string) =>
  yup
    .string()
    .when('contributor', (value: SearchableProfile | undefined, schema) => {
      if (value?.guid) {
        return undefined;
      }
      return pattern
        ? schema.matches(pattern, {
            message: 'Should be 8 digits',
            excludeEmptyString: true,
          })
        : schema.test(
            'validate-ipn',
            'Should be 8 digits',
            testIpn(IPN_MIN_DEFAULT, IPN_MAX_DEFAULT)
          );
    });

export const getContributionFormValidationSchema = (
  validation?: ContributionValidationSchema,
  contribution?: Contribution,
  patchValidation?: ContributionPatchValidationSchema
) => {
  const addressMaxLength =
    patchValidation?.location.properties.address.maxLength || 500;
  const locationNameMaxLength =
    patchValidation?.location.properties.name.maxLength || 255;
  const otherRoleMaxLength =
    (patchValidation?.role as StringValidation)?.maxLength || 40;
  const nameMaxLength = validation?.name.maxLength || 255;

  const isniValidator = isniValidation(patchValidation?.isni?.pattern);
  const ipnValidator = ipnValidation(patchValidation?.ipn?.pattern);

  return yup.object().shape({
    name: yup
      .string()
      .required(fieldValidationMessages.REQUIRED)
      .max(nameMaxLength, fieldValidationMessages.MAX_CHAR(nameMaxLength))
      .trim(),
    contributor: yup
      .object()
      .when('name', {
        is: false,
        then: yup.object().required(fieldValidationMessages.REQUIRED),
      })
      .nullable(),
    date: yup.object().shape({
      date: contribution?.date?.date
        ? yup
            .date()
            .nullable()
            .required(fieldValidationMessages.REQUIRED)
            .typeError('Invalid Date Format')
        : yup.date().nullable().typeError('Invalid Date Format'),
    }),
    profileGuid: yup.string(),
    artistIpn: yup.string().when('className', {
      is: ContributionClassName.ARTIST,
      then: ipnValidator,
    }),
    composerIpi: yup.string().when('className', {
      is: ContributionClassName.SONGWRITER,
      then: yup
        .string()
        .matches(
          patchValidation?.ipi
            ? new RegExp(patchValidation.ipi.pattern)
            : IPIRegex,
          {
            message: 'Invalid IPI',
            excludeEmptyString: true,
          }
        ),
    }),
    producerIpn: yup.string().when('className', {
      is: ContributionClassName.PRODUCTION,
      then: ipnValidator,
    }),
    performerIpn: yup.string().when('className', {
      is: ContributionClassName.PERFORMER,
      then: ipnValidator,
    }),
    artistIsni: yup.string().when('className', {
      is: ContributionClassName.ARTIST,
      then: isniValidator,
    }),
    composerIsni: yup.string().when('className', {
      is: ContributionClassName.SONGWRITER,
      then: isniValidator,
    }),
    producerIsni: yup.string().when('className', {
      is: ContributionClassName.PRODUCTION,
      then: isniValidator,
    }),
    performerIsni: yup.string().when('className', {
      is: ContributionClassName.PERFORMER,
      then: isniValidator,
    }),
    location: yup.object().shape({
      address: yup
        .string()
        .max(
          addressMaxLength,
          fieldValidationMessages.MAX_CHAR(addressMaxLength)
        ),
      name: yup
        .string()
        .max(
          locationNameMaxLength,
          fieldValidationMessages.MAX_CHAR(locationNameMaxLength)
        ),
      country: contribution?.location?.country.countryCode
        ? yup
            .object()
            .shape({ title: yup.string(), value: yup.string() })
            .required(fieldValidationMessages.REQUIRED)
            .nullable()
        : yup
            .object()
            .shape({ title: yup.string(), value: yup.string() })
            .nullable(),
    }),
    // TODO deprecated? couldn't find the actual input field for this validation
    credits: yup.object().shape({
      name: yup.string().max(255, fieldValidationMessages.MAX_CHAR(255)),
      role: yup.string().max(255, fieldValidationMessages.MAX_CHAR(255)),
    }),
    otherRole: yup
      .string()
      .max(
        otherRoleMaxLength,
        fieldValidationMessages.MAX_CHAR(otherRoleMaxLength)
      ),
  });
};
